import React, { useEffect, useRef, useState } from 'react';
import { Plus, ArrowRight, ShieldCheck, Zap } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';

interface ChartPoint {
  year: string;
  value: string;
  x: number;
  y: number;
  isCurrent?: boolean;
  growth: string;
  desc: string;
}

export const SolarGrowthSection: React.FC = () => {
  const [animateLine, setAnimateLine] = useState(false);
  const [activeHoverIdx, setActiveHoverIdx] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const stats = [
    { label: '국내 누적 태양광 발전량', value: '33.0GW', unit: '+', desc: '최근 6년간 90% 이상 가파른 상승세' },
    { label: '전국 설치 지붕 및 발전 부지', value: '20만 개소', unit: '+', desc: '공장, 축사, 창고 지붕 임대 급증' },
    { label: '연간 태양광 전력거래 & 임대 시장', value: '4.5조원', unit: '+', desc: '안정적 고정 임대수익 창출' },
  ];

  const chartPoints: ChartPoint[] = [
    { year: '2020년', value: '17.3GW', x: 50, y: 230, growth: '기준', desc: '전국 태양광 보급 본격화' },
    { year: '2021년', value: '21.2GW', x: 160, y: 185, growth: '+22.5%', desc: '지붕 임대사업 지속 확산' },
    { year: '2022년', value: '24.3GW', x: 270, y: 148, growth: '+14.6%', desc: '기업 ESG / RE100 참여 증가' },
    { year: '2023년', value: '28.0GW', x: 380, y: 105, growth: '+15.2%', desc: '신재생 에너지 전환 가속' },
    { year: '2026년', value: '33.0GW 돌파!', x: 490, y: 45, isCurrent: true, growth: '역대 최고치!', desc: '지붕 수리비 지원 & 임대 수익 전성기' },
  ];

  const pathD = `M ${chartPoints.map((p) => `${p.x},${p.y}`).join(' L ')}`;
  const areaD = `${pathD} L ${chartPoints[chartPoints.length - 1].x},270 L ${chartPoints[0].x},270 Z`;

  // Trigger animation when scrolled into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimateLine(true);
        }
      },
      { threshold: 0.25 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToConsultation = () => {
    const elem = document.getElementById('consultation');
    if (elem) {
      elem.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      ref={sectionRef}
      id="growth-section"
      className="py-20 sm:py-28 bg-slate-50 text-slate-900 relative border-t border-b border-slate-200 overflow-hidden"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <ScrollReveal direction="up">
          <div className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="text-xs sm:text-sm sm:text-base font-bold text-slate-900 mb-3">
                <span>대한민국 태양광 지속 성장</span>
              </div>
              <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight leading-[1.2]">
                '<span className="text-sky-600 font-black">로또지붕</span>' 지금 만드세요!
              </h2>
            </div>
          </div>
        </ScrollReveal>

        {/* Main 2-Column Showcase */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
          
          {/* Left Column (5 Cols): Key Impact Metrics */}
          <div className="lg:col-span-5 space-y-8 sm:space-y-10">
            {stats.map((st, idx) => (
              <ScrollReveal key={idx} direction="up" delay={idx * 0.1}>
                <div className="border-b border-slate-200 pb-6 group">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs sm:text-sm font-bold text-slate-500 block">
                      {st.label}
                    </span>
                    <span className="text-[11px] font-semibold text-sky-600 bg-sky-50 px-2 py-0.5 rounded border border-sky-100 opacity-0 group-hover:opacity-100 transition-opacity">
                      {st.desc}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tight">
                      {st.value}
                    </span>
                    <Plus className="w-6 h-6 sm:w-8 sm:h-8 text-sky-500 stroke-[3]" />
                  </div>
                </div>
              </ScrollReveal>
            ))}

            {/* Quick Callout Info Badge */}
            <ScrollReveal direction="up" delay={0.35}>
              <div className="p-5 rounded-2xl bg-white border border-slate-200 text-xs sm:text-sm text-slate-700 font-medium keep-all flex flex-col items-center justify-center text-center gap-3 shadow-sm">
                <div className="p-2 rounded-xl bg-sky-50 text-sky-600 shrink-0">
                  <Zap className="w-5 h-5 fill-sky-500 text-sky-500" />
                </div>
                <p className="text-center leading-relaxed">
                  전국적으로 태양광 지붕 임대가 급증하여<br />
                  <strong className="text-slate-900 font-extrabold underline decoration-sky-400">33.0GW 역대 최고 발전량</strong>을 기록하고 있습니다.
                </p>
              </div>
            </ScrollReveal>
          </div>

          {/* Right Column (7 Cols): Dynamic Line-Connecting Chart */}
          <div className="lg:col-span-7">
            <ScrollReveal direction="up" delay={0.2}>
              <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-xl relative overflow-hidden">
                
                {/* Header inside Chart Card */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-black text-sky-600 tracking-wider uppercase bg-sky-50 px-3 py-1 rounded-md border border-sky-200 flex items-center gap-1.5">
                      전국 태양광 설치 발전량 추이
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span className="text-xs text-slate-500 font-bold">2020 ~ 2026 대한민국 태양광 발전량</span>
                  </div>
                </div>

                {/* SVG Line Chart Container */}
                <div className="relative w-full h-[320px] sm:h-[370px]">
                  <svg
                    viewBox="0 0 540 320"
                    className="w-full h-full overflow-visible select-none"
                    preserveAspectRatio="xMidYMid meet"
                  >
                    <defs>
                      {/* Linear Gradient for Shaded Area */}
                      <linearGradient id="chartGradientSky" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0EA5E9" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#0EA5E9" stopOpacity="0" />
                      </linearGradient>

                      {/* Glowing Line Gradient */}
                      <linearGradient id="lineGlowSky" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#0284C7" />
                        <stop offset="60%" stopColor="#0EA5E9" />
                        <stop offset="100%" stopColor="#38BDF8" />
                      </linearGradient>
                    </defs>

                    {/* Horizontal Grid Lines */}
                    {[
                      { y: 270, label: '15GW' },
                      { y: 200, label: '20GW' },
                      { y: 130, label: '25GW' },
                      { y: 60, label: '30GW+' },
                    ].map((g, i) => (
                      <g key={i}>
                        <line
                          x1="30"
                          y1={g.y}
                          x2="510"
                          y2={g.y}
                          stroke="#E2E8F0"
                          strokeWidth="1"
                          strokeDasharray="4 4"
                        />
                        <text
                          x="22"
                          y={g.y + 4}
                          textAnchor="end"
                          fontSize="10"
                          fontWeight="700"
                          fill="#94A3B8"
                        >
                          {g.label}
                        </text>
                      </g>
                    ))}

                    {/* Area Under Curve - Smoothly Fades In */}
                    <path
                      d={areaD}
                      fill="url(#chartGradientSky)"
                      style={{
                        opacity: animateLine ? 1 : 0,
                        transition: 'opacity 1.5s ease-out 0.3s',
                      }}
                    />

                    {/* Main Connecting Trend Line - Animated Drawing along points */}
                    <path
                      d={pathD}
                      fill="none"
                      stroke="url(#lineGlowSky)"
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeDasharray="600"
                      strokeDashoffset={animateLine ? 0 : 600}
                      style={{
                        transition: 'stroke-dashoffset 2s cubic-bezier(0.25, 1, 0.5, 1)',
                      }}
                      className="drop-shadow-[0_4px_12px_rgba(14,165,233,0.4)]"
                    />

                    {/* Nodes & Labels - Animated pop-in sequentially as line connects */}
                    {chartPoints.map((p, idx) => {
                      const isHovered = activeHoverIdx === idx;
                      // Sequence delay matching line drawing animation timing
                      const delayMs = idx * 380 + 100;

                      return (
                        <g
                          key={idx}
                          className={`cursor-pointer transition-all duration-500 ease-out ${
                            animateLine ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                          }`}
                          style={{
                            transitionDelay: animateLine ? `${delayMs}ms` : '0ms',
                            transformOrigin: `${p.x}px ${p.y}px`,
                          }}
                          onMouseEnter={() => setActiveHoverIdx(idx)}
                          onMouseLeave={() => setActiveHoverIdx(null)}
                        >
                          {/* Vertical Guide Line */}
                          <line
                            x1={p.x}
                            y1={p.y}
                            x2={p.x}
                            y2="270"
                            stroke={p.isCurrent ? '#0EA5E9' : '#CBD5E1'}
                            strokeWidth={p.isCurrent ? '2' : '1'}
                            strokeDasharray="3 3"
                            opacity={p.isCurrent || isHovered ? 0.9 : 0.4}
                          />

                          {/* Node Circle */}
                          {p.isCurrent ? (
                            <g>
                              <circle
                                cx={p.x}
                                cy={p.y}
                                r="10"
                                fill="#0284C7"
                                stroke="#38BDF8"
                                strokeWidth="3.5"
                                className="drop-shadow-[0_0_10px_rgba(14,165,233,0.8)]"
                              />
                              <circle cx={p.x} cy={p.y} r="3.5" fill="#FFFFFF" />
                            </g>
                          ) : (
                            <g>
                              <circle
                                cx={p.x}
                                cy={p.y}
                                r={isHovered ? 8 : 6}
                                fill="#FFFFFF"
                                stroke="#0EA5E9"
                                strokeWidth={isHovered ? 4 : 3}
                                className="transition-all duration-200 drop-shadow-sm"
                              />
                            </g>
                          )}

                          {/* Capacity Label Floating Above Node */}
                          <g transform={`translate(${p.x}, ${p.y - 16})`}>
                            {p.isCurrent && (
                              <g transform="translate(0, -24)">
                                <rect
                                  x="-46"
                                  y="-12"
                                  width="92"
                                  height="24"
                                  rx="12"
                                  fill="#0284C7"
                                />
                                <text
                                  x="0"
                                  y="4"
                                  textAnchor="middle"
                                  fill="#FFFFFF"
                                  fontSize="13"
                                  fontWeight="900"
                                >
                                  역대 최고치!
                                </text>
                              </g>
                            )}

                            <text
                              x="0"
                              y="0"
                              textAnchor="middle"
                              fill={p.isCurrent ? '#0284C7' : '#1E293B'}
                              fontSize={p.isCurrent ? '14' : '12'}
                              fontWeight={p.isCurrent || isHovered ? '900' : '700'}
                            >
                              {p.value}
                            </text>
                          </g>

                          {/* Year Label at Baseline */}
                          <text
                            x={p.x}
                            y="295"
                            textAnchor="middle"
                            fill={p.isCurrent ? '#0284C7' : '#64748B'}
                            fontSize={p.isCurrent || isHovered ? '13' : '11'}
                            fontWeight={p.isCurrent || isHovered ? '900' : '700'}
                          >
                            {p.year}
                          </text>

                          {/* Hover Tooltip */}
                          {isHovered && (
                            <foreignObject
                              x={p.x - 70}
                              y={Math.max(10, p.y - 95)}
                              width="140"
                              height="75"
                              className="overflow-visible pointer-events-none"
                            >
                              <div className="bg-slate-900/95 backdrop-blur-md text-white rounded-xl p-2.5 shadow-2xl border border-slate-700 text-center text-xs animate-in fade-in zoom-in-95 duration-150">
                                <div className="font-extrabold text-sky-400">{p.year} ({p.value})</div>
                                <div className="text-[10px] text-slate-300 mt-0.5">{p.desc}</div>
                                <div className="inline-block mt-1 px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-300 font-bold text-[10px]">
                                  증가율: {p.growth}
                                </div>
                              </div>
                            </foreignObject>
                          )}
                        </g>
                      );
                    })}
                  </svg>
                </div>

                {/* Mobile Responsive Grid */}
                <div className="mt-4 sm:hidden grid grid-cols-2 gap-2 pt-3 border-t border-slate-100">
                  {chartPoints.map((p, idx) => (
                    <div
                      key={idx}
                      className={`p-2.5 rounded-xl border text-xs flex flex-col transition-all ${
                        p.isCurrent
                          ? 'bg-sky-50 border-sky-300 text-sky-800 shadow-sm'
                          : 'bg-slate-50 border-slate-200 text-slate-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-slate-500 font-bold">{p.year}</span>
                        <span className="text-[9px] font-black text-sky-600 bg-white px-1.5 py-0.2 rounded border border-sky-100">
                          {p.growth}
                        </span>
                      </div>
                      <span className="font-black text-slate-900 text-sm mt-1">{p.value}</span>
                    </div>
                  ))}
                </div>

              </div>
            </ScrollReveal>
          </div>

        </div>

        {/* Bottom CTA Banner */}
        <ScrollReveal direction="up" delay={0.3}>
          <div className="bg-sky-50/80 rounded-2xl sm:rounded-3xl p-5 sm:p-8 border border-sky-200 flex flex-col sm:flex-row items-center justify-between gap-5 sm:gap-6 shadow-sm">
            <div className="flex items-center gap-3.5 sm:gap-4 w-full sm:w-auto">
              <div className="p-3 sm:p-3.5 rounded-2xl bg-sky-500 text-white shrink-0 font-black shadow-md shadow-sky-500/20">
                <ShieldCheck className="w-6 h-6 sm:w-7 sm:h-7" />
              </div>
              <div className="space-y-1">
                <h4 className="text-base sm:text-lg font-black text-slate-900 leading-snug">
                  방치했던 지붕 고민, 이제 <span className="text-sky-600">'로또지붕'</span>으로 바꾸세요!
                </h4>
                <p className="text-xs sm:text-sm text-slate-600 keep-all font-medium">
                  플러스에스디 전문 팀이 100% 무료로 지붕 현장 상태 및 20년 예상 임대 수익을 진단해 드립니다.
                </p>
              </div>
            </div>

            <button
              onClick={scrollToConsultation}
              className="w-full sm:w-auto justify-center shrink-0 px-6 py-3.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-black text-xs sm:text-sm transition-all shadow-lg shadow-sky-500/25 active:scale-95 cursor-pointer flex items-center gap-2 whitespace-nowrap"
            >
              <span>무료 현장 수익 진단받기</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </ScrollReveal>

      </div>
    </section>
  );
};

